import * as THREE from "three"
import { OrbitControls } from "three/addons/controls/OrbitControls.js"

export default class Publication3D {
  constructor(element, options = {}) {
    this.container = element
    this.frontCover = options.frontCover || ""
    this.spine = options.spine || ""
    this.backCover = options.backCover || ""
    this.outerSpine = options.outerSpine || ""
    this.width = options.width || 210 // A4 width in mm
    this.height = options.height || 297 // A4 height in mm
    this.spineWidth = options.spineWidth || 5 // spine width in mm

    this.init()
  }

  init() {
    // Create scene
    this.scene = new THREE.Scene()
    this.scene.background = new THREE.Color(0x807e4f)

    // Create camera
    this.camera = new THREE.PerspectiveCamera(
      50,
      this.container.clientWidth / this.container.clientHeight,
      0.1,
      1000,
    )
    this.camera.position.z = 500
    this.camera.position.y = 20

    this.setupRenderer()
    this.setupControls()
    this.setupLighting()
    this.createBook()

    // Start animation
    this.animate()

    // Handle window resize
    window.addEventListener("resize", () => this.onWindowResize())
  }

  setupRenderer() {
    // Create renderer with enhanced quality settings
    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
      powerPreference: "high-performance",
      preserveDrawingBuffer: true,
    })
    this.renderer.setSize(
      this.container.clientWidth,
      this.container.clientHeight,
    )
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2)) // Balance between quality and performance
    this.renderer.shadowMap.enabled = true
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap
    this.renderer.outputColorSpace = THREE.SRGBColorSpace
    this.container.appendChild(this.renderer.domElement)
  }

  setupControls() {
    // Add controls with proper damping and rotation center
    this.controls = new OrbitControls(this.camera, this.renderer.domElement)
    this.controls.enableDamping = true
    this.controls.dampingFactor = 0.1 // Reduced for smoother motion
    this.controls.rotateSpeed = 0.5
    this.controls.enableZoom = true
    this.controls.minDistance = 200
    this.controls.maxDistance = 800
    // Set the target to the center of the scene to ensure proper rotation
    this.controls.target.set(0, 0, 0)
  }

  setupLighting() {
    // Create a light container to keep lights in fixed positions
    this.lightRig = new THREE.Group()
    this.scene.add(this.lightRig)

    // High intensity ambient light for base illumination
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6)
    this.scene.add(ambientLight)

    // Main directional light (key light)
    const mainLight = new THREE.DirectionalLight(0xffffff, 2.5)
    mainLight.position.set(-3, 5, 3)
    mainLight.castShadow = true
    mainLight.shadow.mapSize.width = 2048
    mainLight.shadow.mapSize.height = 2048
    mainLight.shadow.bias = -0.0001
    this.lightRig.add(mainLight)

    // Fill light to reduce harsh shadows
    const fillLight = new THREE.DirectionalLight(0xffffff, 1.2)
    fillLight.position.set(3, 3, 3)
    this.lightRig.add(fillLight)

    // Bottom light for extra dimension
    const bottomLight = new THREE.DirectionalLight(0xffffff, 0.4)
    bottomLight.position.set(0, -3, 1)
    this.lightRig.add(bottomLight)
  }

  // Optimize a texture with standard settings
  optimizeTexture(texture) {
    // Set to sRGB color space
    texture.colorSpace = THREE.SRGBColorSpace

    // Improve texture quality
    texture.generateMipmaps = true
    texture.minFilter = THREE.LinearMipmapLinearFilter
    texture.magFilter = THREE.LinearFilter
    texture.anisotropy = this.renderer.capabilities.getMaxAnisotropy()

    return texture
  }

  // Create materials for the book faces
  createMaterials() {
    // Enhanced texture loader with better filtering
    const textureLoader = new THREE.TextureLoader()

    // Base white material
    const whiteMaterial = new THREE.MeshStandardMaterial({
      color: 0xffffff,
      side: THREE.DoubleSide,
      roughness: 0.1,
      metalness: 0,
    })

    // Create standard material with texture
    const createEnhancedMaterial = (texturePath) => {
      if (!texturePath) return whiteMaterial

      const texture = textureLoader.load(texturePath)
      this.optimizeTexture(texture)

      return new THREE.MeshStandardMaterial({
        map: texture,
        side: THREE.DoubleSide,
        roughness: 0.3,
        metalness: 0.1,
        emissive: new THREE.Color(0x111111),
        emissiveMap: texture,
        emissiveIntensity: 0.15,
      })
    }

    // Create repeating texture material
    const createRepeatingTextureMaterial = (texturePath, repeatX, repeatY) => {
      if (!texturePath) return whiteMaterial

      const texture = textureLoader.load(texturePath)
      this.optimizeTexture(texture)

      // Set texture to repeat
      texture.wrapS = THREE.RepeatWrapping
      texture.wrapT = THREE.RepeatWrapping
      texture.repeat.set(repeatX, repeatY)

      // Rotate texture by 90 degrees
      texture.rotation = Math.PI / 2
      texture.center = new THREE.Vector2(0.5, 0.5)

      return new THREE.MeshStandardMaterial({
        map: texture,
        side: THREE.DoubleSide,
        roughness: 0.3,
        metalness: 0.1,
      })
    }

    // Create all required materials
    return {
      front: this.frontCover
        ? createEnhancedMaterial(this.frontCover)
        : whiteMaterial,
      back: this.backCover
        ? createEnhancedMaterial(this.backCover)
        : whiteMaterial,
      spine: this.spine ? createEnhancedMaterial(this.spine) : whiteMaterial,
      edges: this.outerSpine
        ? createRepeatingTextureMaterial(
            this.outerSpine,
            this.width / 50,
            this.height / 50,
          )
        : whiteMaterial,
    }
  }

  createBook() {
    const book = new THREE.Group()

    // Dimensions
    const w = this.width
    const h = this.height
    const d = this.spineWidth

    // Get all materials
    const materials = this.createMaterials()

    // Create materials array for BoxGeometry faces
    const bookMaterials = [
      materials.front, // Front face
      materials.back, // Back face
      materials.edges, // Right face (fore edge)
      materials.edges, // Top face (fore edge)
      materials.spine, // Left face (spine)
      materials.edges, // Bottom face (fore edge)
    ]

    // Create geometry and mesh
    const geometry = new THREE.BoxGeometry(d, h, w)
    const bookMesh = new THREE.Mesh(geometry, bookMaterials)

    // Enable shadows
    bookMesh.castShadow = true
    bookMesh.receiveShadow = true

    book.add(bookMesh)

    // Initial position of the book at the center of the scene
    book.position.set(0, 0, 0)

    // Initial rotation for a nice view
    book.rotation.y = -290
    book.rotation.x = Math.PI / -20
    book.rotation.z = -100

    this.scene.add(book)
    this.book = book
  }

  animate() {
    requestAnimationFrame(() => this.animate())

    // Update controls
    this.controls.update()

    // Ensure lighting stays fixed relative to camera
    this.lightRig.quaternion.copy(this.camera.quaternion)

    // Render scene
    this.renderer.render(this.scene, this.camera)
  }

  onWindowResize() {
    this.camera.aspect =
      this.container.clientWidth / this.container.clientHeight
    this.camera.updateProjectionMatrix()
    this.renderer.setSize(
      this.container.clientWidth,
      this.container.clientHeight,
    )
  }
}

window.addEventListener("load", () => {
  // Wait a bit to ensure all other scripts are initialized
  setTimeout(() => {
    const publicationElements = document.querySelectorAll(
      ".dimensional-publication",
    )

    publicationElements.forEach((element) => {
      new Publication3D(element, {
        height: element.dataset.height,
        width: element.dataset.width,
        backCover: element.dataset.backCover,
        spine: element.dataset.spine,
        frontCover: element.dataset.frontCover,
        spineWidth: element.dataset.spineWidth,
        outerSpine: element.dataset.outerSpine,
      })
    })
  }, 100) // 100ms delay
})
